import React, { useEffect, useState } from 'react';
import { useMfa } from '../hooks/useMfa';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';

type Step = 'loading' | 'qr' | 'verify';

function renderQrCode(qrCode: string) {
  if (!qrCode) return null;
  if (qrCode.startsWith('data:') || qrCode.startsWith('http')) {
    return (
      <img
        src={qrCode}
        alt="QR code para Google Authenticator"
        className="mx-auto h-48 w-48 rounded-lg bg-white p-2"
      />
    );
  }
  return (
    <div
      dangerouslySetInnerHTML={{ __html: qrCode }}
      className="mx-auto flex h-48 w-48 items-center justify-center rounded-lg bg-white p-2 [&_svg]:h-full [&_svg]:w-full"
      aria-label="QR code para Google Authenticator"
    />
  );
}

const MfaEnroll: React.FC = () => {
  const { prepareEnrollment, verifyEnrollment, loading, error } = useMfa();
  const { refreshMfaGate, login } = useAuth();
  const [step, setStep] = useState<Step>('loading');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    let cancelled = false;
    prepareEnrollment()
      .then((result) => {
        if (cancelled) return;
        if (result.kind === 'already_active') {
          void refreshMfaGate();
          window.history.replaceState(null, '', '/auth/mfa-challenge');
          return;
        }
        setFactorId(result.factorId);
        setQrCode(result.qrCode);
        setSecret(result.secret);
        setStep('qr');
      })
      .catch(() => {
        if (!cancelled) setStep('qr');
      });
    return () => { cancelled = true; };
  }, [prepareEnrollment, refreshMfaGate]);

  const handleVerify = async () => {
    if (code.length !== 6 || !factorId) return;
    setVerifyError('');
    try {
      await verifyEnrollment(factorId, code);
      await refreshMfaGate();
      login();
    } catch (err: unknown) {
      setVerifyError(err instanceof Error ? err.message : 'Código inválido');
      setCode('');
    }
  };

  const handleCodeChange = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) setTimeout(() => void handleVerify(), 50);
  };

  if (step === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <Spinner color="blue" size="lg" />
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted p-6">
      <article className="w-full max-w-md rounded-2xl bg-background p-8 shadow-lg ring-1 ring-border">
        <header className="mb-6 text-center">
          <h1 className="text-xl font-bold text-foreground">Autenticação em 2 etapas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Proteja sua conta com Google Authenticator
          </p>
        </header>

        {step === 'qr' && (
          <section>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              1. Escaneie o QR code
            </h2>
            {renderQrCode(qrCode)}
            {!qrCode && secret && (
              <p className="text-sm text-muted-foreground">
                QR indisponível. Use o código manual abaixo no app.
              </p>
            )}
            {secret && (
              <div className="mt-4 rounded-lg bg-muted p-3">
                <p className="mb-1 text-xs font-medium text-muted-foreground">Código manual</p>
                <code className="block break-all font-mono text-sm text-foreground select-all">
                  {secret}
                </code>
              </div>
            )}
            {error && (
              <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>
            )}
            <Button
              type="button"
              className="mt-6 w-full"
              disabled={!factorId}
              onClick={() => setStep('verify')}
            >
              Já escaneei → Próximo
            </Button>
          </section>
        )}

        {step === 'verify' && (
          <section>
            <h2 className="mb-1 text-sm font-semibold text-foreground">
              2. Digite o código de 6 dígitos
            </h2>
            <input
              id="totp-enroll"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="000000"
              className="mt-4 w-full min-h-16 rounded-lg border border-border bg-background text-center font-mono text-4xl tracking-[0.5em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            {(verifyError || error) && (
              <p role="alert" className="mt-3 text-sm text-destructive">{verifyError || error}</p>
            )}
            <Button
              type="button"
              className="mt-4 w-full"
              disabled={loading || code.length !== 6 || !factorId}
              onClick={() => void handleVerify()}
            >
              {loading ? 'Ativando...' : 'Ativar MFA'}
            </Button>
          </section>
        )}
      </article>
    </main>
  );
};

export default MfaEnroll;
